/**
 * Componente AnimatedText
 * Texto con animaciones de entrada letra por letra o palabra por palabra
 * Inspirado en el estilo de sitios premium tipo Awwwards
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Texto animado con efectos de revelado
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.text - Texto a animar
 * @param {string} props.as - Elemento HTML a renderizar ('h1', 'h2', 'p', etc.)
 * @param {string} props.animation - Tipo de animación ('chars', 'words', 'lines', 'wave')
 * @param {number} props.delay - Retraso inicial
 * @param {number} props.staggerDelay - Retraso entre elementos
 * @param {string} props.className - Clases CSS adicionales
 * @param {boolean} props.triggerOnce - Si la animación solo debe ejecutarse una vez
 * @returns {JSX.Element} Texto animado
 */
const AnimatedText = ({
  text,
  as: Component = 'p',
  animation = 'words',
  delay = 0,
  staggerDelay = 0.03,
  className = '',
  triggerOnce = true,
}) => {
  const containerRef = useRef(null);
  const elementsRef = useRef([]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Limpiar contenido previo
    container.innerHTML = '';
    elementsRef.current = [];

    let items = [];

    // Crear elementos según el tipo de animación
    if (animation === 'chars' || animation === 'wave') {
      // Dividir en caracteres
      const chars = text.split('');
      chars.forEach((char) => {
        const span = document.createElement('span');
        span.className = 'inline-block';
        span.textContent = char === ' ' ? '\u00A0' : char;
        span.style.willChange = 'transform, opacity';
        container.appendChild(span);
        items.push(span);
      });
    } else if (animation === 'words') {
      // Dividir en palabras
      const words = text.split(' ');
      words.forEach((word, index) => {
        const wrapper = document.createElement('span');
        wrapper.className = 'inline-block overflow-visible';
        wrapper.style.paddingBottom = '0.1em'; // Espacio para letras descendentes (g, y, p, q)
        
        const span = document.createElement('span');
        span.className = 'inline-block';
        span.textContent = word;
        span.style.willChange = 'transform, opacity';
        
        wrapper.appendChild(span);
        container.appendChild(wrapper);
        
        // Añadir espacio después de cada palabra excepto la última
        if (index < words.length - 1) {
          const space = document.createTextNode('\u00A0');
          container.appendChild(space);
        }
        
        items.push(span);
      });
    } else if (animation === 'lines') {
      // Animar todo como un bloque
      const span = document.createElement('span');
      span.className = 'inline-block';
      span.textContent = text;
      span.style.willChange = 'transform, opacity';
      container.appendChild(span);
      items.push(span);
    }

    elementsRef.current = items;

    // Configurar animación según el tipo
    let animationConfig;
    
    if (animation === 'wave') {
      // Animación tipo ola
      animationConfig = {
        from: { y: 100, opacity: 0, rotateX: -90 },
        to: { 
          y: 0, 
          opacity: 1, 
          rotateX: 0,
          ease: 'back.out(1.7)',
          stagger: {
            each: staggerDelay,
            from: 'start',
          },
        },
      };
    } else {
      // Animación estándar de subida
      animationConfig = {
        from: { y: '100%', opacity: 0 },
        to: { 
          y: '0%', 
          opacity: 1,
          ease: 'power4.out',
          stagger: staggerDelay,
        },
      };
    }

    // Establecer estado inicial
    gsap.set(items, animationConfig.from);

    // Crear animación con ScrollTrigger
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: container,
        start: 'top 85%',
        toggleActions: triggerOnce ? 'play none none none' : 'play none none reverse',
      },
    });

    tl.to(items, {
      ...animationConfig.to,
      duration: 0.8,
      delay,
    });

    // Cleanup
    return () => {
      tl.kill();
    };
  }, [text, animation, delay, staggerDelay, triggerOnce]);

  return (
    <Component 
      ref={containerRef} 
      className={`overflow-visible ${className}`}
      aria-label={text}
    />
  );
};

AnimatedText.propTypes = {
  text: PropTypes.string.isRequired,
  as: PropTypes.string,
  animation: PropTypes.oneOf(['chars', 'words', 'lines', 'wave']),
  delay: PropTypes.number,
  staggerDelay: PropTypes.number,
  className: PropTypes.string,
  triggerOnce: PropTypes.bool,
};

export default AnimatedText;
